import {
	ChangeDetectorRef,
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	EventEmitter,
	forwardRef,
	Input,
	OnDestroy,
	Output,
	inject
} from '@angular/core';
import * as dayjs from 'dayjs';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import {
	LibraryCollection,
	LibraryCollectionItem,
	LibraryCollectionItemsAndFolders,
	LibraryFolder
} from '@go-modules/interfaces/library/library-collection';
import { ContentTypeType } from '@go-modules/interfaces/library/library-types';
import {
	CONTENT_TYPES,
	MODES,
	SORT_OPTIONS
} from '@go-modules/components/library/library-collections-viewer/library-collection-viewer.constants';
import {
	CollectionFolderViewerModule
} from '@go-modules/components/library/collection-folder-viewer/collection-folder-viewer.module';
import {
	CollectionItemViewerModule
} from '@go-modules/components/library/collection-item-viewer/collection-item-viewer.module';
import { Types } from '@go-modules/services/media/media-types';
import { NgxMediaService } from '@go-modules/services/media/media.service';
import { LibraryTreeDataSource } from './library-tree-datasource';
import { LibraryTreeNode, NodeType } from '@go-modules/components/library/library-tree-viewer/library-tree-node';
import { OrgSettings } from '@go-modules/interfaces/groups/org-settings';

type TreeGridCellTarget = 'select' | 'name' | 'modified' | 'actions';
type TreeGridFocusTarget = 'row' | TreeGridCellTarget;

@Component({
	selector: 'ngx-library-tree-viewer',
	templateUrl: './library-tree-viewer.component.html',
	styleUrls: ['./library-tree-viewer.component.scss'],
	imports: [
		MatMenuModule,
		MatIconModule,
		MatCheckboxModule,
		FormsModule,
		MatProgressBarModule,
		TranslatePipe,
		MatButtonModule,
		forwardRef(() => CollectionFolderViewerModule),
		forwardRef(() => CollectionItemViewerModule)
	],
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class LibraryTreeViewerComponent implements OnDestroy {
	public readonly treeGridHeaderIds = {
		select: 'library-tree-header-select',
		name: 'library-tree-header-name',
		modified: 'library-tree-header-modified',
		actions: 'library-tree-header-actions'
	} as const;

	@Input() public collectionItems: LibraryCollectionItemsAndFolders;
	@Input() public selectedCollection: LibraryCollection;
	@Input() public selectedContentType: ContentTypeType;
	@Input() public collections: LibraryCollection[];
	@Input() public archiveCollection: LibraryCollection;
	@Input() public mode: string;
	@Input() public multiSelect: boolean = false;
	@Input() public sortBy: string = SORT_OPTIONS.LASTMODIFIED;
	@Input() public searchStr: string = '';
	@Input() public orgSettings: OrgSettings;
	@Input() public expandedHeight: number = 370;
	@Input() public currentlySelected: { id?: number; hash?: string; folderId?: number; };
	@Input() public selectAllChecked: boolean = false;
	@Input() public hideOnArchiveAndManaged: boolean = false;

	@Output() public selectItem = new EventEmitter<any>();
	@Output() public setCurrentlySelected = new EventEmitter<any>();
	@Output() public loadNewItems = new EventEmitter<any>();
	@Output() public setActivityPanelIsOpen = new EventEmitter<boolean>();
	@Output() public setSelectAllChecked = new EventEmitter<boolean>();
	@Output() public toggleSelectAll = new EventEmitter<boolean>();
	@Output() public copyCheckedItems = new EventEmitter<void>();
	@Output() public moveCheckedItems = new EventEmitter<void>();
	@Output() public removeCheckedItems = new EventEmitter<void>();

	public MODES = MODES;
	public CONTENT_TYPES = CONTENT_TYPES;
	public dataSource = inject(LibraryTreeDataSource);
	private changeDetectorRef = inject(ChangeDetectorRef);
	private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
	private mediaService = inject(NgxMediaService);
	private translate = inject(TranslateService);
	private focusedRowKey: string | null = null;
	private focusedTarget: TreeGridFocusTarget = 'row';
	private dataSourceSubscription = this.dataSource.data$.subscribe(() => {
		this.changeDetectorRef.markForCheck();
	});

	public ngOnDestroy (): void {
		this.dataSourceSubscription.unsubscribe();
		this.dataSource.disconnect();
	}

	public handleExpandedChange (node: LibraryTreeNode, expanded: boolean): void {
		if (expanded && !this.dataSource.isExpanded(node)) {
			this.dataSource.expand(node);
		} else if (!expanded && this.dataSource.isExpanded(node)) {
			this.dataSource.collapse(node);
		}
	}

	public isFolderEmpty (node: LibraryTreeNode): boolean {
		if (node.type !== NodeType.Folder || node.isLoading) {
			return false;
		}

		if (!this.dataSource.isExpanded(node)) {
			return false;
		}

		const nodeIndex = this.dataSource.data.indexOf(node);
		if (nodeIndex === -1 || nodeIndex === this.dataSource.data.length - 1) {
			return true;
		}

		const nextNode = this.dataSource.data[nodeIndex + 1];
		return nextNode == null || nextNode.level <= node.level;
	}

	public trackNode = (_index: number, node: LibraryTreeNode): string => {
		return node.key;
	};

	public getTreeAriaMultiselectable (): 'true' | 'false' {
		return this.multiSelect ? 'true' : 'false';
	}

	public getTreeAriaColCount (): number {
		return this.multiSelect ? 4 : 3;
	}

	public getTreeColspan (): number {
		return this.getTreeAriaColCount();
	}

	public getTreeGridHeaderId (column: keyof typeof this.treeGridHeaderIds): string {
		return this.treeGridHeaderIds[column];
	}

	public getNodeElementId (node: LibraryTreeNode): string {
		return `library-tree-node-${node.key}`;
	}

	public getNodeAriaSelected (node: LibraryTreeNode): 'true' | 'false' | null {
		if (this.multiSelect) {
			return null;
		}

		return this.isNodeSelected(node) ? 'true' : 'false';
	}

	public getNodeAriaChecked (node: LibraryTreeNode): 'true' | 'false' | null {
		if (!this.multiSelect || node.type !== NodeType.Item) {
			return null;
		}

		return node.checked ? 'true' : 'false';
	}

	public getNodeAriaLabel (node: LibraryTreeNode): string {
		if (node.type === NodeType.Folder) {
			const folder = node.data as LibraryFolder;
			return this.translate.instant('collection-folder-aria-label', {
				name: folder.name,
				date: dayjs(folder.updated_at).format('MMM D, YYYY')
			});
		}

		const item = node.data as LibraryCollectionItem;
		return this.translate.instant(this.getItemAriaLabelKey(item), {
			name: item.name,
			date: dayjs(item.modified_at).format('MMM D, YYYY')
		});
	}

	public getNodeAriaExpanded (node: LibraryTreeNode): 'true' | 'false' | null {
		if (node.type !== NodeType.Folder) {
			return null;
		}

		return this.dataSource.isExpanded(node) ? 'true' : 'false';
	}

	public getFolderNode (node: LibraryTreeNode): LibraryTreeNode<typeof NodeType.Folder> {
		return node as LibraryTreeNode<typeof NodeType.Folder>;
	}

	public getFolderData (node: LibraryTreeNode): LibraryFolder {
		return this.getFolderNode(node).data;
	}

	public getItemNode (node: LibraryTreeNode): LibraryTreeNode<typeof NodeType.Item> {
		return node as LibraryTreeNode<typeof NodeType.Item>;
	}

	public getItemData (node: LibraryTreeNode): LibraryCollectionItem {
		return this.getItemNode(node).data;
	}

	public getItemHash (node: LibraryTreeNode): string {
		return this.getItemData(node).hash;
	}

	public getNodeAriaPosInSet (node: LibraryTreeNode): number {
		return this.getSiblingNodes(node).findIndex((sibling) => sibling.key === node.key) + 1;
	}

	public getNodeAriaSetSize (node: LibraryTreeNode): number {
		return this.getSiblingNodes(node).length;
	}

	public getRowTabindex (node: LibraryTreeNode): number {
		return this.getActiveRowKey() === node.key ? 0 : -1;
	}

	public handleRowFocus (nodeKey: string): void {
		this.focusedRowKey = nodeKey;
		this.focusedTarget = 'row';
	}

	public handleCellFocus (nodeKey: string, target: TreeGridCellTarget): void {
		this.focusedRowKey = nodeKey;
		this.focusedTarget = target;
	}

	public getCellTabindex (node: LibraryTreeNode, target: 'name' | 'modified'): number {
		const activeState = this.getActiveFocusState();
		return activeState?.key === node.key && activeState.target === target ? 0 : -1;
	}

	public getActionTabindex (node: LibraryTreeNode): number {
		return this.getActiveRowKey() === node.key ? 0 : -1;
	}

	public getCheckboxTabindex (node: LibraryTreeNode): number | null {
		if (!this.multiSelect) {
			return null;
		}

		return this.getActionTabindex(node);
	}

	public handleRowKeydown (event: KeyboardEvent, node: LibraryTreeNode): void {
		if (event.target !== event.currentTarget) {
			return;
		}

		const nodeIndex = this.dataSource.data.findIndex((currentNode) => currentNode.key === node.key);
		if (nodeIndex < 0) {
			return;
		}

		switch (event.key) {
			case 'ArrowDown':
				event.preventDefault();
				this.focusRowByIndex(nodeIndex + 1);
				break;
			case 'ArrowUp':
				event.preventDefault();
				this.focusRowByIndex(nodeIndex - 1);
				break;
			case 'ArrowRight':
				event.preventDefault();
				this.handleRowArrowRight(node);
				break;
			case 'ArrowLeft':
				event.preventDefault();
				this.handleArrowLeft(node, nodeIndex);
				break;
			case 'Enter':
			case ' ':
			case 'Space':
			case 'Spacebar':
				event.preventDefault();
				this.handleNodeActivation(node);
				break;
			case 'Home':
				event.preventDefault();
				this.focusRowByIndex(0);
				break;
			case 'End':
				event.preventDefault();
				this.focusRowByIndex(this.dataSource.data.length - 1);
				break;
			default:
				break;
		}
	}

	public handleCellKeydown (
		event: KeyboardEvent,
		node: LibraryTreeNode,
		target: TreeGridCellTarget
	): void {
		if (target !== 'select' && event.target !== event.currentTarget) {
			return;
		}

		const nodeIndex = this.dataSource.data.findIndex((currentNode) => currentNode.key === node.key);
		if (nodeIndex < 0) {
			return;
		}

		switch (event.key) {
			case 'ArrowDown':
				event.preventDefault();
				this.focusRowTargetByIndex(nodeIndex + 1, target);
				break;
			case 'ArrowUp':
				event.preventDefault();
				this.focusRowTargetByIndex(nodeIndex - 1, target);
				break;
			case 'ArrowRight':
				event.preventDefault();
				this.focusNextTarget(node.key, target);
				break;
			case 'ArrowLeft':
				event.preventDefault();
				this.focusPreviousTarget(node, nodeIndex, target);
				break;
			case 'Home':
				event.preventDefault();
				this.focusRowTargetByIndex(0, target);
				break;
			case 'End':
				event.preventDefault();
				this.focusRowTargetByIndex(this.dataSource.data.length - 1, target);
				break;
			case 'Enter':
			case ' ':
			case 'Space':
			case 'Spacebar':
				if (target === 'select') {
					event.preventDefault();
					this.toggleNodeChecked(node);
				} else if (target !== 'actions') {
					event.preventDefault();
					this.handleNodeActivation(node);
				}
				break;
			default:
				break;
		}
	}

	public isItemDetailsLoading (itemHash: string): boolean {
		return this.dataSource.isItemDetailsLoading(itemHash);
	}

	public isNodeCurrentlySelected (node: LibraryTreeNode): boolean {
		return this.isNodeSelected(node);
	}

	public handleNodeActivation (node: LibraryTreeNode): void {
		const selector = node.type === NodeType.Folder
			? `[data-tree-node-key="${node.key}"] .library-folder-info`
			: `[data-tree-node-key="${node.key}"] .library-item-info`;
		const target = this.elementRef.nativeElement.querySelector<HTMLElement>(selector);
		target?.click();
	}

	public handleSelectItem (event: any): void {
		this.selectItem.emit(event);
	}

	private isNodeSelected (node: LibraryTreeNode): boolean {
		if (node.type === NodeType.Folder) {
			return this.currentlySelected?.id === (node.data as LibraryFolder).id;
		}

		return this.currentlySelected?.hash === (node.data as LibraryCollectionItem).hash;
	}

	private getSiblingNodes (node: LibraryTreeNode): LibraryTreeNode[] {
		const parentId = this.dataSource.getParentId(node);

		return this.dataSource.data.filter((currentNode) =>
			currentNode.level === node.level && this.dataSource.getParentId(currentNode) === parentId
		);
	}

	private handleRowArrowRight (node: LibraryTreeNode): void {
		if (node.type === NodeType.Folder && !this.dataSource.isExpanded(node)) {
			this.dataSource.expand(node);
			return;
		}

		this.focusFirstCell(node.key);
	}

	private toggleNodeChecked (node: LibraryTreeNode): void {
		if (!this.multiSelect || node.type !== NodeType.Item) {
			return;
		}

		this.dataSource.updateItemChecked(this.getItemHash(node), !node.checked);
		this.setSelectAllChecked.emit(this.areAllVisibleItemsChecked());
	}

	private areAllVisibleItemsChecked (): boolean {
		const visibleItemNodes = this.dataSource.data.filter((node) => node.type === NodeType.Item);
		return visibleItemNodes.length > 0 && visibleItemNodes.every((node) => node.checked);
	}

	private handleArrowLeft (node: LibraryTreeNode, nodeIndex: number): void {
		if (node.type === NodeType.Folder && this.dataSource.isExpanded(node)) {
			this.dataSource.collapse(node);
			return;
		}

		const parentIndex = this.findParentIndex(nodeIndex);
		if (parentIndex >= 0) {
			this.focusRowByIndex(parentIndex);
		}
	}

	private findParentIndex (childIndex: number): number {
		const child = this.dataSource.data[childIndex];
		if (child == null || child.level === 0) {
			return -1;
		}

		for (let index = childIndex - 1; index >= 0; index--) {
			if (this.dataSource.data[index].level === child.level - 1) {
				return index;
			}
		}

		return -1;
	}

	private focusRowByIndex (nodeIndex: number): void {
		if (nodeIndex < 0 || nodeIndex >= this.dataSource.data.length) {
			return;
		}

		this.focusRowByKey(this.dataSource.data[nodeIndex].key);
	}

	private focusRowByKey (nodeKey: string): void {
		this.focusTarget(nodeKey, 'row');
	}

	private focusRowTargetByIndex (
		nodeIndex: number,
		target: TreeGridFocusTarget
	): void {
		if (nodeIndex < 0 || nodeIndex >= this.dataSource.data.length) {
			return;
		}

		const nodeKey = this.dataSource.data[nodeIndex].key;
		if (!this.focusTarget(nodeKey, target)) {
			this.focusRowByKey(nodeKey);
		}
	}

	private focusFirstCell (nodeKey: string): void {
		this.focusPreferredTarget(
			nodeKey,
			this.multiSelect ? ['select', 'name', 'modified', 'actions', 'row'] : ['name', 'modified', 'actions', 'row']
		);
	}

	private focusNextTarget (nodeKey: string, target: TreeGridCellTarget): void {
		switch (target) {
			case 'select':
				this.focusPreferredTarget(nodeKey, ['name', 'modified', 'actions', 'row']);
				break;
			case 'name':
				this.focusPreferredTarget(nodeKey, ['modified', 'actions', 'row']);
				break;
			case 'modified':
				this.focusPreferredTarget(nodeKey, ['actions', 'row']);
				break;
			case 'actions':
			default:
				break;
		}
	}

	private focusPreviousTarget (
		node: LibraryTreeNode,
		nodeIndex: number,
		target: TreeGridCellTarget
	): void {
		switch (target) {
			case 'select':
				this.focusRowByKey(node.key);
				break;
			case 'name':
				this.focusPreferredTarget(node.key, this.multiSelect ? ['select', 'row'] : ['row']);
				break;
			case 'modified':
				this.focusPreferredTarget(node.key, this.multiSelect ? ['name', 'select', 'row'] : ['name', 'row']);
				break;
			case 'actions':
				this.focusPreferredTarget(
					node.key,
					this.multiSelect ? ['modified', 'name', 'select', 'row'] : ['modified', 'name', 'row']
				);
				break;
			default:
				this.handleArrowLeft(node, nodeIndex);
				break;
		}
	}

	private focusPreferredTarget (
		nodeKey: string,
		targets: TreeGridFocusTarget[]
	): boolean {
		return targets.some((target) => this.focusTarget(nodeKey, target));
	}

	private focusTarget (nodeKey: string, target: TreeGridFocusTarget): boolean {
		const element = this.getFocusableElement(nodeKey, target);
		if (element == null) {
			return false;
		}

		this.focusedRowKey = nodeKey;
		this.focusedTarget = target;
		element.focus();
		return true;
	}

	private getFocusableElement (
		nodeKey: string,
		target: TreeGridFocusTarget
	): HTMLElement | null {
		const row = this.elementRef.nativeElement.querySelector<HTMLElement>(`tr[data-tree-node-key="${nodeKey}"]`);
		if (row == null) {
			return null;
		}

		if (target === 'row') {
			return row;
		}

		if (target === 'select') {
			const checkbox = row.querySelector<HTMLElement>('[data-tree-cell="select"] input[type="checkbox"]');
			return this.isVisibleFocusableElement(checkbox) ? checkbox : null;
		}

		const element = row.querySelector<HTMLElement>(`[data-tree-cell="${target}"]`);
		return this.isVisibleFocusableElement(element) ? element : null;
	}

	private isVisibleFocusableElement (element: HTMLElement | null): element is HTMLElement {
		if (element == null) {
			return false;
		}

		const styles = window.getComputedStyle(element);
		return styles.display !== 'none' && styles.visibility !== 'hidden';
	}

	private getActiveFocusState (): { key: string; target: TreeGridFocusTarget; } | null {
		const visibleRows = this.dataSource.data;
		if (visibleRows.length === 0) {
			return null;
		}

		const activeRow = visibleRows.find((visibleNode) => visibleNode.key === this.focusedRowKey) ?? visibleRows[0];
		const target = activeRow.key === this.focusedRowKey ? this.focusedTarget : 'row';

		return {
			key: activeRow.key,
			target
		};
	}

	private getActiveRowKey (): string | null {
		return this.getActiveFocusState()?.key ?? null;
	}

	private getItemAriaLabelKey (item: LibraryCollectionItem): string {
		const filename = item.filename;
		const mediaType = filename != null && filename !== ''
			? this.mediaService.determineMediaType(filename)
			: Types.VIDEO;

		switch (item.collection_item_type) {
			case CONTENT_TYPES.RUBRICS:
				return 'library-collections_content-label-rubric';
			case CONTENT_TYPES.MARKERS:
				return 'library-collections_content-label-markers';
			case CONTENT_TYPES.MEDIA:
				if (item.media_type === Types.AUDIO) {
					return 'library-collections_content-label-audio';
				}
				return mediaType === Types.VIDEO
					? 'library-collections_content-label-video'
					: 'library-collections_content-label-audio';
			case CONTENT_TYPES.DOCUMENTS:
				return mediaType === Types.DOCUMENT
					? 'library-collections_content-label-document'
					: 'library-collections_content-label-image';
			case CONTENT_TYPES.ACTIVITIES:
			default:
				return 'library-collections_content-label-activities';
		}
	}
}
